/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useTranslation } from 'react-i18next'
import { Zap, Coins, Users, Wrench, ArrowRight } from 'lucide-react'
import { AnimateInView } from '@/components/animate-in-view'

interface ArchitectureProps {
  className?: string
}

export function Architecture(_props: ArchitectureProps) {
  const { t } = useTranslation()

  const features = [
    {
      icon: <Zap className='size-5 text-orange-500' />,
      title: t('High Performance'),
      desc: t('Support for high concurrency with automatic load balancing'),
    },
    {
      icon: <Coins className='size-5 text-yellow-500' />,
      title: t('Transparent Billing'),
      desc: t('Pay-as-you-go with real-time usage monitoring'),
    },
    {
      icon: <Users className='size-5 text-blue-500' />,
      title: t('Team Collaboration'),
      desc: t('Multi-user management with flexible permission allocation'),
    },
    {
      icon: <Wrench className='size-5 text-gray-500' />,
      title: t('Easy to Extend'),
      desc: t('Standard protocol, seamless integration with existing tools'),
    },
  ]

  return (
    <section className='relative z-10 px-6 py-12 md:py-16'>
      <div className='mx-auto max-w-6xl'>
        <AnimateInView className='mb-16 text-center'>
          <h2 className='text-3xl font-bold tracking-tight md:text-4xl'>
            {t('Aggregation Hub')} · {t('Token Transit Architecture')}
          </h2>
          <p className='text-muted-foreground mt-3 text-sm'>
            {t('One-stop solution for AI model integration, routing, and billing')}
          </p>
        </AnimateInView>

        {/* Architecture diagram */}
        <AnimateInView
          animation='scale-in'
          className='border-border/40 bg-muted/20 mx-auto max-w-4xl rounded-2xl border p-8 md:p-12'
        >
          <div className='flex flex-col items-center gap-8 md:flex-row md:items-center md:justify-center md:gap-6'>
            {/* Your Application */}
            <div className='flex flex-col items-center'>
              <div className='bg-red-600 text-white rounded-xl px-6 py-4 text-center shadow-lg'>
                <div className='text-sm font-semibold'>{t('Your Application')}</div>
                <div className='text-xs opacity-80'>Your Application</div>
              </div>
            </div>

            {/* Arrow */}
            <ArrowRight className='size-6 text-muted-foreground hidden md:block' />
            <ArrowRight className='size-6 text-muted-foreground rotate-90 md:hidden' />

            {/* AI Token Gateway */}
            <div className='flex flex-col items-center'>
              <div className='border-red-500/30 bg-red-500/10 rounded-xl px-6 py-4 text-center shadow-lg'>
                <div className='text-sm font-semibold text-red-600 dark:text-red-400'>
                  {t('AI Token Gateway')}
                </div>
                <div className='text-xs text-muted-foreground'>{t('Intelligent Routing')}</div>
                <div className='mt-2 flex gap-1.5 justify-center'>
                  <span className='bg-red-500/20 text-red-600 dark:text-red-400 rounded px-2 py-0.5 text-[10px]'>
                    {t('Auth')}
                  </span>
                  <span className='bg-red-500/20 text-red-600 dark:text-red-400 rounded px-2 py-0.5 text-[10px]'>
                    {t('Routing')}
                  </span>
                  <span className='bg-red-500/20 text-red-600 dark:text-red-400 rounded px-2 py-0.5 text-[10px]'>
                    {t('Billing')}
                  </span>
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className='hidden flex-col items-center gap-1 md:flex'>
              <span className='text-[10px] text-muted-foreground font-medium'>
                {t('Intelligent Routing')}
              </span>
              <ArrowRight className='size-6 text-muted-foreground' />
            </div>
            <ArrowRight className='size-6 text-muted-foreground rotate-90 md:hidden' />

            {/* AI Model Cluster */}
            <div className='border-red-500/30 bg-red-500/5 rounded-xl px-6 py-4 text-center shadow-lg'>
              <div className='text-sm font-semibold text-red-600 dark:text-red-400 mb-3'>
                {t('AI Model Cluster')}
              </div>
              <div className='space-y-2 text-xs'>
                <div className='flex items-center gap-2'>
                  <span className='bg-red-500/20 text-red-600 dark:text-red-400 rounded px-1.5 py-0.5 text-[10px]'>
                    {t('Domestic')}
                  </span>
                  <span className='text-muted-foreground'>DeepSeek · 混元 · 通义 · 文心</span>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='bg-red-500/20 text-red-600 dark:text-red-400 rounded px-1.5 py-0.5 text-[10px]'>
                    {t('Domestic')}
                  </span>
                  <span className='text-muted-foreground'>GLM · 星火 · 百川 · MiniMax</span>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='bg-red-500/20 text-red-600 dark:text-red-400 rounded px-1.5 py-0.5 text-[10px]'>
                    {t('Overseas')}
                  </span>
                  <span className='text-muted-foreground'>OpenAI · Claude · Gemini</span>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='bg-red-500/20 text-red-600 dark:text-red-400 rounded px-1.5 py-0.5 text-[10px]'>
                    {t('Overseas')}
                  </span>
                  <span className='text-muted-foreground'>Llama · Mistral · Cohere</span>
                </div>
              </div>
            </div>
          </div>

          {/* Return arrow from AI Model Cluster to Your Application */}
          <div className='mt-8 flex justify-center'>
            <svg className='w-full max-w-4xl h-6' viewBox='0 0 800 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              {/* Path: from right (AI Model Cluster) down, left, then up to arrow head */}
              <path
                d='M 680 0 L 680 12 L 120 20 L 120 10'
                stroke='#dc2626'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              {/* Arrow head pointing up at Your Application side */}
              <polygon
                points='120,4 114,10 126,10'
                fill='#dc2626'
              />
              {/* Return label badge */}
              <rect x='355' y='2' width='90' height='20' rx='4' fill='#dc2626' />
              <text x='400' y='16' textAnchor='middle' fill='white' fontSize='12' fontWeight='500'>
                {t('Return')}
              </text>
            </svg>
          </div>
        </AnimateInView>

        {/* Feature cards */}
        <div className='mt-12 grid grid-cols-2 gap-6 md:grid-cols-4'>
          {features.map((f, i) => (
            <AnimateInView
              key={f.title}
              delay={i * 100}
              animation='fade-up'
              className='border-border/40 bg-muted/20 rounded-xl border p-5 text-center'
            >
              <div className='mb-3 flex justify-center'>{f.icon}</div>
              <h3 className='mb-1 text-sm font-semibold'>{f.title}</h3>
              <p className='text-muted-foreground text-xs leading-relaxed'>
                {f.desc}
              </p>
            </AnimateInView>
          ))}
        </div>
      </div>
    </section>
  )
}
